import type { ComposeScore } from "@plenum/shared/api";
import { useState } from "react";
import {
  ApiError,
  useComposeCheck,
  useComposeHumanize,
  useCreateOutreach,
  useSite,
} from "../../lib/api";
import { useToast } from "../Toast";

/**
 * Outreach composer (Q72). The reviewer writes or pastes an email, scores it with ZeroGPT's AI
 * detector, rewrites it with ZeroGPT's humanizer when the score is high, copies it out, and logs it
 * as a "contacted" event against the chosen contact. Plenum never sends mail. The panel explains
 * itself when the deployment has no ZeroGPT key.
 */
export function ComposePanel({ siteId }: { siteId: number }) {
  const site = useSite(siteId);
  const check = useComposeCheck();
  const humanize = useComposeHumanize();
  const logEvent = useCreateOutreach();
  const toast = useToast();
  const [contactId, setContactId] = useState<number | "">("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [score, setScore] = useState<ComposeScore | null>(null);
  const [previous, setPrevious] = useState<ComposeScore | null>(null);
  const [strength, setStrength] = useState<"Quality" | "Balanced" | "More Human">("Balanced");
  const [unavailable, setUnavailable] = useState<string | null>(null);

  const contacts = (site.data?.contacts ?? []).filter(
    (c) => !c.do_not_contact && c.review_status !== "rejected",
  );
  const chosen = contacts.find((c) => c.id === contactId);
  const tooShort = body.trim().length < 20;
  const busy = check.isPending || humanize.isPending;

  function onError(e: unknown) {
    if (e instanceof ApiError && e.status === 503) {
      setUnavailable(e.message);
      return;
    }
    toast(e instanceof ApiError ? e.message : "Request failed", "danger");
  }

  async function runCheck() {
    try {
      const r = await check.mutateAsync({ text: body.trim() });
      setPrevious(null);
      setScore(r.score);
    } catch (e) {
      onError(e);
    }
  }

  async function runHumanize() {
    try {
      const r = await humanize.mutateAsync({ text: body.trim(), strength });
      setBody(r.text);
      setPrevious(r.before);
      setScore(r.after);
      toast(`Humanized: AI share ${r.before.ai_percent}% to ${r.after.ai_percent}%`);
    } catch (e) {
      onError(e);
    }
  }

  async function copyOut() {
    const text = subject.trim() ? `Subject: ${subject.trim()}\n\n${body}` : body;
    try {
      await navigator.clipboard.writeText(text);
      toast("Copied");
    } catch {
      toast("Copy failed; select the text and copy it by hand", "danger");
    }
  }

  async function logSent() {
    try {
      await logEvent.mutateAsync({
        site_id: siteId,
        contact_id: typeof contactId === "number" ? contactId : undefined,
        status: "contacted",
        channel: "email",
        note: [
          chosen ? `Emailed ${chosen.full_name}` : "Emailed",
          subject.trim() ? `"${subject.trim()}"` : null,
          score ? `(AI share ${score.ai_percent}%)` : null,
        ]
          .filter(Boolean)
          .join(" ")
          .slice(0, 1000),
      });
      toast("Logged as contacted");
    } catch (e) {
      onError(e);
    }
  }

  return (
    <section data-testid="compose-panel">
      <h3 className="eyebrow mb-1">Compose outreach</h3>
      <p className="mb-2 text-[12px]" style={{ color: "var(--muted)" }}>
        Write or paste the email, check how much of it reads as AI-written, humanize it if the share
        is high, then copy it into your mail client. Nothing is sent from here.
      </p>
      {unavailable ? (
        <p
          className="mb-2 rounded border px-2 py-1 text-[12px]"
          style={{ borderColor: "var(--gold)", color: "var(--paper)" }}
          data-testid="compose-unavailable"
        >
          {unavailable}. The runbook's "Outreach composer" section covers the setup.
        </p>
      ) : null}
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap gap-2">
          <select
            className="input min-w-[220px]"
            value={contactId}
            onChange={(e) => setContactId(e.target.value ? Number(e.target.value) : "")}
            aria-label="Recipient"
            data-testid="compose-contact"
          >
            <option value="">Recipient (optional)</option>
            {contacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.full_name}
                {c.title ? `, ${c.title}` : ""}
                {c.email ? ` <${c.email}>` : " (no email on file)"}
              </option>
            ))}
          </select>
          <input
            className="input flex-1"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject"
            aria-label="Subject"
            data-testid="compose-subject"
          />
        </div>
        <textarea
          className="input min-h-[160px] w-full font-[inherit] text-[13px] leading-relaxed"
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            setScore(null);
            setPrevious(null);
          }}
          placeholder="Email body"
          aria-label="Email body"
          data-testid="compose-body"
        />
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn btn-sm"
            disabled={busy || tooShort}
            onClick={runCheck}
            data-testid="compose-check"
          >
            {check.isPending ? "Checking…" : "Check AI score"}
          </button>
          <select
            className="input"
            value={strength}
            onChange={(e) => setStrength(e.target.value as typeof strength)}
            aria-label="Humanizer strength"
          >
            {["Quality", "Balanced", "More Human"].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-sm"
            disabled={busy || tooShort}
            onClick={runHumanize}
            data-testid="compose-humanize"
          >
            {humanize.isPending ? "Humanizing…" : "Humanize"}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={tooShort}
            onClick={copyOut}
            data-testid="compose-copy"
          >
            Copy
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={tooShort || logEvent.isPending}
            onClick={logSent}
            data-testid="compose-log"
          >
            Log as sent
          </button>
          {score ? <ScoreBadge score={score} previous={previous} /> : null}
        </div>
        {score && score.flagged.length > 0 ? (
          <details className="text-[12px]" data-testid="compose-flagged">
            <summary style={{ color: "var(--muted)" }}>
              {score.flagged.length} sentence{score.flagged.length === 1 ? "" : "s"} read as
              AI-written
            </summary>
            <ul className="mt-1 flex flex-col gap-1 pl-4">
              {score.flagged.map((s) => (
                <li key={s} style={{ color: "var(--muted)" }}>
                  {s}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </div>
    </section>
  );
}

function ScoreBadge({ score, previous }: { score: ComposeScore; previous: ComposeScore | null }) {
  const tone = score.ai_percent <= 20 ? "ok" : score.ai_percent <= 50 ? "gold" : "danger";
  const color = tone === "ok" ? "var(--ok)" : tone === "gold" ? "var(--gold)" : "var(--danger)";
  return (
    <span
      className="num rounded border px-2 py-0.5 text-[12px]"
      style={{ borderColor: color, color }}
      title={score.feedback ?? undefined}
      data-testid="compose-score"
    >
      {previous ? `${previous.ai_percent}% → ` : ""}
      {score.ai_percent}% AI
      {score.words ? ` · ${score.ai_words ?? 0} of ${score.words} words` : ""}
    </span>
  );
}
